import { useEffect, useMemo, useRef, useState } from "react";
import { useCatalog } from "../lib/catalog-context";
import { filterPlugins, extractCategories } from "../lib/search";
import FilterBar from "../components/FilterBar";
import PluginCard from "../components/PluginCard";
import PluginListView from "../components/PluginListView";
import { formatDataThroughDate } from "../lib/adoption";
import CatalogToolbar from "../components/CatalogToolbar";
import type { SortOption, ViewMode } from "../components/CatalogToolbar";
import SearchBar from "../components/SearchBar";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import GettingStarted from "../components/GettingStarted";
import type { ExtensionType, MarketplaceEntry, SupportedClient } from "../types/index";
import styles from "./CatalogPage.module.css";

const CLIENT_FILTER_OPTIONS: { value: SupportedClient; label: string }[] = [
  { value: "claude-chat",   label: "Claude Chat" },
  { value: "claude-cowork", label: "Claude Cowork" },
  { value: "claude-code",   label: "Claude Code" },
];

type TypeFilter = "all" | ExtensionType;

const TYPE_FILTER_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all",    label: "All" },
  { value: "plugin", label: "Plugins" },
  { value: "skill",  label: "Skills" },
];

const SORT_ORDER_MAP: Record<SortOption, "asc" | "desc"> = {
  relevance: "desc", // unused — relevance keeps the search engine's order, no comparator runs
  topRated: "desc", // ignored by the topRated sort (unrated entries are always last)
  name: "asc",
  updated: "desc",
  created: "desc",
  official: "desc", // ignored by the official sort (Official-first is a fixed order)
  mostUsed: "desc", // ignored by the mostUsed sort (unmeasured entries are always last)
};

export const PAGE_SIZE = 30;

function getEmptyStateMessage(
  query: string,
  clients: SupportedClient[],
  categories: string[],
  typeFilter: TypeFilter = "all",
  officialOnly = false,
): string {
  const noun =
    typeFilter === "plugin" ? "plug-ins"
    : typeFilter === "skill" ? "skills"
    : "plug-ins or skills";
  const parts: string[] = [];
  if (query) parts.push(`matching "${query}"`);
  if (officialOnly) parts.push("marked Official CAI");
  if (clients.length > 0) {
    const labels = clients.map((c) => CLIENT_FILTER_OPTIONS.find((o) => o.value === c)?.label ?? c);
    parts.push(`for ${labels.join(" or ")}`);
  }
  if (categories.length === 1) parts.push(`in ${categories[0]}`);
  else if (categories.length > 1) parts.push(`in ${categories.join(" or ")}`);
  return parts.length > 0 ? `No ${noun} ${parts.join(" ")}` : `No ${noun} available`;
}

interface CatalogPageProps {
  onSelectPlugin: (plugin: MarketplaceEntry) => void;
  initialClients?: string[];
  initialCategories?: string[];
  initialQuery?: string;
  initialType?: ExtensionType;
  initialOfficial?: boolean;
  onHelp?: () => void;
  onFiltersChange?: (query: string, clients: SupportedClient[], categories: string[], type?: ExtensionType, official?: boolean) => void;
}

export default function CatalogPage({ onSelectPlugin, initialClients, initialCategories, initialQuery, initialType, initialOfficial, onHelp, onFiltersChange }: CatalogPageProps) {
  // Catalog data from the shared provider (one fetch, one source of truth).
  const { plugins, isLoading, error, refresh, adoptionMeta } = useCatalog();
  // "" whenever there is no adoption artifact or the date is unparseable — the caption
  // is then omitted. Both cases are live today: #264 publishes a real artifact in
  // NON-PROD (adoption-refresh runs daily at 08:00 UTC), while prod leaves
  // `snowflake_usage_view` unset, so its Lambda is heartbeat-only and no artifact
  // exists. So this caption renders in non-prod and is absent in prod until the prod
  // usage view is enabled.
  const usageAsOf = adoptionMeta?.dataThroughDate ? formatDataThroughDate(adoptionMeta.dataThroughDate) : "";

  // Sorted view of the catalog — updated whenever the provider's plugins change.
  const allPlugins = useMemo(
    () =>
      [...plugins].sort((a, b) =>
        a.pluginName.toLowerCase().localeCompare(b.pluginName.toLowerCase()),
      ),
    [plugins],
  );

  // Categories derived from the full sorted list.
  const availableCategories = useMemo(() => extractCategories(allPlugins), [allPlugins]);

  const [clientFilters, setClientFilters] = useState<SupportedClient[]>((initialClients as SupportedClient[]) ?? []);
  const [categoryFilters, setCategoryFilters] = useState<string[]>(initialCategories ?? []);
  const [searchQuery, setSearchQuery] = useState(initialQuery ?? "");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(initialType ?? "all");
  // Two defaults, one per mode:
  //   browsing  -> Most Used   (product decision, 2026-08-10)
  //   searching -> Best Match  (product decision, 2026-08-19, shipped in #591)
  //
  // Seeded from the URL as well as set on transition, so a shared link that already
  // carries a query lands on Best Match instead of flashing Most Used for a frame.
  //
  // Entries with no usage are not hidden or lumped together arbitrarily: ties preserve
  // the incoming order, which while browsing is name order (allPlugins is sorted by
  // name below), so the tail of the list reads exactly as the Name sort would.
  //
  // NOTE: #266 AC 11 gates the Most Used default on measured coverage above 50%. The gate
  // FAILED and the default is flipped anyway — a deliberate override, not an oversight
  // (2026-08-10, Bryce + Veronica; see the PR description).
  //
  // The override was agreed against the 40.7% pre-production spike. #264's producer has
  // since measured the real figure at 42.4% (649/1532 entries, see contract-7-adoption.ts):
  // the gate still fails, by the same margin and for the same structural reason — Anthropic
  // counts a skill only when explicitly invoked, so reference skills never register. That
  // 42.4% is close to the ceiling for a per-skill metric, so this waiver is not waiting on
  // a matching improvement that would let the gate pass on its own terms.
  const [sortBy, setSortBy] = useState<SortOption>(
    (initialQuery ?? "").trim() ? "relevance" : "mostUsed",
  );
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [officialOnly, setOfficialOnly] = useState(initialOfficial ?? false);
  const [searchBarClearToken, setSearchBarClearToken] = useState(0);

  const hasQuery = searchQuery.trim().length > 0;
  // The sort to hand back when the search box empties, so browsing resumes where it
  // left off instead of snapping back to the default. Seeded with the browse default so
  // a page that loaded straight into a search still lands on Most Used when cleared.
  const preSearchSortRef = useRef<SortOption>("mostUsed");
  const hadQueryRef = useRef(hasQuery);

  // Best Match takes over when a query appears and steps aside when it clears.
  //
  // Keyed on the empty <-> non-empty transition rather than on every query change:
  // searchQuery updates per keystroke, so reasserting "relevance" on each change
  // would overwrite a sort the user deliberately picked partway through searching.
  // sortBy is in the deps only so the reads below are current — a sortBy change on
  // its own leaves hasQuery unchanged and exits at the guard.
  useEffect(() => {
    if (hasQuery === hadQueryRef.current) return;
    hadQueryRef.current = hasQuery;

    if (hasQuery) {
      preSearchSortRef.current = sortBy;
      setSortBy("relevance");
    } else if (sortBy === "relevance") {
      // Only reclaim the remembered sort if Best Match is still the selection. A sort
      // chosen during the search was an explicit choice and outlives the query.
      setSortBy(preSearchSortRef.current);
    }
  }, [hasQuery, sortBy]);

  // Filtered results derived from the catalog + active filters. Deriving (rather
  // than holding in state and syncing via effects) keeps results correct by
  // construction — including after a refresh() that updates plugins in place.
  const results = useMemo(
    () =>
      filterPlugins(allPlugins, {
        query: searchQuery,
        // Strict match first, broaden to OR only when strict returns nothing (#801).
        // Without this the default is "strict", so a multi-word query with no single
        // entry containing every word returns zero — the exact regression this PR fixes.
        mode: "auto",
        supportedClients: clientFilters.length > 0 ? clientFilters : undefined,
        categories: categoryFilters.length > 0 ? categoryFilters : undefined,
        extensionType: typeFilter === "all" ? undefined : typeFilter,
        officialOnly,
        sortBy,
        sortOrder: SORT_ORDER_MAP[sortBy],
      }),
    [allPlugins, searchQuery, clientFilters, categoryFilters, typeFilter, sortBy, officialOnly],
  );

  // Sync active filters to the URL on user changes. Skip the initial render so we
  // don't echo the URL-derived seed values straight back. Sort is intentionally
  // not synced (matches prior behaviour).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    onFiltersChange?.(
      searchQuery,
      clientFilters,
      categoryFilters,
      typeFilter === "all" ? undefined : typeFilter,
      officialOnly,
    );
    // onFiltersChange identity is not a filter input; fire only on filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, clientFilters, categoryFilters, typeFilter, officialOnly]);

  const handleSearch = (query: string) => setSearchQuery(query);

  // Reset the visible window to the first page whenever the filtered results change.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [results]);

  const sentinelVisible = visibleCount < results.length;
  // Keyed on `sentinelVisible` (a boolean), not `visibleCount`: the observer is
  // recreated only when crossing the "more to show / nothing more" boundary, so
  // each PAGE_SIZE grow reuses the same observer instead of tearing it down and
  // rebuilding it every batch. Keying on `visibleCount` regressed once (commit
  // d762e41): because visibleCount changes on every grow, the effect re-ran each
  // batch — disconnecting and rebuilding a fresh IntersectionObserver every time
  // instead of leaving one in place. The boolean key holds it stable across grows.
  //
  // `viewMode` is in the deps as defensive hardening, not a fix for a live bug.
  // Today the grid and list branches both render the sentinel at the same index
  // of a keyless fragment, so React reuses the same DOM node across a view switch
  // and the observer stays bound to a connected element (verified: the marked node
  // survives the switch, still connected). But that reuse is an implicit invariant
  // of the JSX shape — a future refactor that changes it would remount the sentinel
  // and strand the observer. Re-running on `viewMode` re-observes the current node
  // regardless; the cost is one disconnect/observe per (rare) manual view toggle.
  useEffect(() => {
    if (!sentinelVisible) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, results.length));
        }
      },
      { rootMargin: "400px" }, // prefetch the next page ~400px before the sentinel scrolls into view
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelVisible, results.length, viewMode]);

  const toggleFilter = <T,>(prev: T[], value: T, eq: (a: T, b: T) => boolean): T[] =>
    prev.some((v) => eq(v, value)) ? prev.filter((v) => !eq(v, value)) : [...prev, value];

  const handleClientToggle = (client: SupportedClient) =>
    setClientFilters((prev) => toggleFilter(prev, client, (a, b) => a === b));

  const handleCategoryToggle = (category: string) =>
    setCategoryFilters((prev) =>
      toggleFilter(prev, category, (a, b) => a.toLowerCase() === b.toLowerCase()),
    );

  const handleTypeChange = (type: TypeFilter) => setTypeFilter(type);

  const handleSortChange = (sort: SortOption) => setSortBy(sort);

  const handleOfficialToggle = () => setOfficialOnly((prev) => !prev);

  if (error) {
    return (
      <div className={styles.errorState}>
        <h1>Error Loading Marketplace</h1>
        <p className={styles.errorMessage}>{error}</p>
        <button onClick={() => refresh()} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.header}>
        <h1>AI Marketplace</h1>
        <p className={styles.subtitle}>
          Browse and discover available AI plug-ins and skills for Cox Auto approved LLM clients.
        </p>
      </div>

      <GettingStarted onHelp={onHelp} />
      <SearchBar onSearch={handleSearch} isLoading={isLoading} externalQuery={searchQuery} clearToken={searchBarClearToken}  placeholder="Search plug-ins and skills..." />
      <p className={styles.totals}>
        Total available: {allPlugins.filter((p) => p.extensionType === "skill").length} skills · {allPlugins.filter((p) => p.extensionType === "plugin").length} plug-ins
      </p>

      <CatalogToolbar
        sortBy={sortBy}
        onSortChange={handleSortChange}
        showRelevance={hasQuery}
        viewMode={viewMode}
        onViewChange={setViewMode}
        officialOnly={officialOnly}
        onOfficialToggle={handleOfficialToggle}
      >
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>
            Type:
          </span>
          <div role="group" aria-label="Filter by type" className={styles.toggleGroup}>
            {TYPE_FILTER_OPTIONS.map((opt) => {
              const active = typeFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleTypeChange(opt.value)}
                  aria-pressed={active}
                  className={`${styles.toggleButton}${active ? ` ${styles.active}` : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>
            Works with:
          </span>
          <div role="group" aria-label="Filter by client" className={styles.toggleGroup}>
            {CLIENT_FILTER_OPTIONS.map((opt) => {
              const active = clientFilters.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => handleClientToggle(opt.value)}
                  aria-pressed={active}
                  className={`${styles.toggleButton}${active ? ` ${styles.active}` : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <FilterBar
          categories={availableCategories}
          activeCategories={categoryFilters}
          onCategoryToggle={handleCategoryToggle}
          labelMinWidth="6rem"
        />
      </CatalogToolbar>

      {/* Stated once here rather than repeated on every card: the window lags "now" by
          ~3 days, and #266 requires that be legible wherever the figure appears. This
          line sits above both the grid and the list view, so it covers both. */}
      {usageAsOf && (
        <p className={styles.usageAsOf}>Usage data as of {usageAsOf}</p>
      )}

      {isLoading && !allPlugins.length ? (
        <Loading message="Loading catalog..." />
      ) : results.length === 0 ? (
        <EmptyState message={getEmptyStateMessage(searchQuery, clientFilters, categoryFilters, typeFilter, officialOnly)}>
          {(searchQuery || clientFilters.length > 0 || categoryFilters.length > 0 || typeFilter !== "all" || officialOnly) && (
            <button
              onClick={() => {
                setClientFilters([]);
                setCategoryFilters([]);
                setSearchQuery("");
                setTypeFilter("all");
                setOfficialOnly(false);
                setSearchBarClearToken((t) => t + 1);
              }}
              className={styles.clearButton}
            >
              Clear filters
            </button>
          )}
        </EmptyState>
      ) : viewMode === "list" ? (
        <>
          <PluginListView plugins={results.slice(0, visibleCount)} onSelectPlugin={onSelectPlugin} />
          {sentinelVisible && <div ref={sentinelRef} aria-hidden="true" />}
        </>
      ) : (
        <>
          <div className={styles.grid}>
            {results.slice(0, visibleCount).map((plugin) => (
              <PluginCard
                key={plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}
                plugin={plugin}
                onClick={() => onSelectPlugin(plugin)}
                onClientClick={handleClientToggle}
                onCategoryClick={handleCategoryToggle}
              />
            ))}
          </div>
          {sentinelVisible && <div ref={sentinelRef} aria-hidden="true" />}
        </>
      )}
    </div>
  );
}
